import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  FolderKanban,
  LayoutGrid,
  List,
  CheckCircle,
  Clock,
  ArrowRight,
  ClipboardList,
  Edit2,
  Trash2,
  RefreshCw,
  ExternalLink,
  Sparkles,
  Archive,
  RotateCcw,
  ArrowLeft,
  PieChart,
  Rows3,
  SlidersHorizontal,
} from "lucide-react";
import { ExpandableCard } from "../common/ExpandableCard";
import { Opportunity, OpportunityStage, QualifyChecklist } from "../../types";
import {
  subscribeToOpportunities,
  createOpportunity,
  updateOpportunity,
  transitionOpportunityStage,
  saveQualifyChecklist,
  deleteOpportunity,
  archiveOpportunity,
  unarchiveOpportunity,
} from "../../services/opportunitiesService";
import { useStaffDirectory } from "../../services/staffService";
import { OpportunityModal } from "../pipeline/OpportunityModal";
import { QualifyModal } from "../pipeline/QualifyModal";
import { PostMortemModal } from "../pipeline/PostMortemModal";
import { ArchiveOpportunityModal } from "../pipeline/ArchiveOpportunityModal";
import { DeleteOpportunityModal } from "../pipeline/DeleteOpportunityModal";
import { HarvestLibraryModal } from "../workspace/HarvestLibraryModal";
import { WinLossAnalyticsModal } from "../pipeline/WinLossAnalyticsModal";
import { PipelineKanban } from "../pipeline/PipelineKanban";
import { StageHistoryTimeline } from "../pipeline/StageHistoryTimeline";
import { UserProfile, BidOutcome } from "../../types";

export interface PipelineViewProps {
  user?: UserProfile | null;
}

export const PipelineView: React.FC<PipelineViewProps> = ({ user = null }) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"cards" | "kanban">("cards");
  const [kanbanCardView, setKanbanCardView] = useState<"standard" | "detailed">(() => {
    try {
      return (
        (localStorage.getItem("phq_kanban_card_view") as "standard" | "detailed") || "standard"
      );
    } catch {
      return "standard";
    }
  });

  const handleKanbanCardViewChange = (mode: "standard" | "detailed") => {
    setKanbanCardView(mode);
    try {
      localStorage.setItem("phq_kanban_card_view", mode);
    } catch (err) {
      console.warn("Failed to persist kanban view mode:", err);
    }
  };

  // Modal States
  const [isOppModalOpen, setIsOppModalOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState<Opportunity | null>(null);

  const [isQualifyModalOpen, setIsQualifyModalOpen] = useState(false);
  const [qualifyingOpp, setQualifyingOpp] = useState<Opportunity | null>(null);

  const [postMortemOpp, setPostMortemOpp] = useState<Opportunity | null>(null);
  const [postMortemInitialOutcome, setPostMortemInitialOutcome] = useState<BidOutcome>("won");

  const [harvestOpp, setHarvestOpp] = useState<Opportunity | null>(null);
  const [archiveOpp, setArchiveOpp] = useState<Opportunity | null>(null);
  const [deleteOpp, setDeleteOpp] = useState<Opportunity | null>(null);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);

  const { staff } = useStaffDirectory();
  const currentUser = {
    displayName: user?.displayName || "User",
    email: user?.email || "mark@mlconnections.com",
  };

  useEffect(() => {
    const unsubscribe = subscribeToOpportunities(
      (items) => {
        setOpportunities(items);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load opportunities:", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter((opp) => {
      // 1. Stage filter
      if (stageFilter === "all") {
        if (opp.stage === "archived") return false;
      } else if (stageFilter === "archived") {
        if (opp.stage !== "archived") return false;
      } else if (opp.stage !== stageFilter) {
        return false;
      }

      // 2. Search filter
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesClient = opp.client.toLowerCase().includes(query);
        const matchesTitle = opp.title.toLowerCase().includes(query);
        const matchesOwner = opp.owner.toLowerCase().includes(query);
        const matchesService = opp.services.some((s) => s.toLowerCase().includes(query));

        if (!matchesClient && !matchesTitle && !matchesOwner && !matchesService) {
          return false;
        }
      }

      return true;
    });
  }, [opportunities, stageFilter, searchTerm]);

  const handleSaveOpportunity = async (
    data: Omit<Opportunity, "id" | "createdAt" | "updatedAt" | "stageHistory">,
  ) => {
    if (editingOpp) {
      await updateOpportunity(editingOpp.id, data);
    } else {
      await createOpportunity(data, currentUser);
    }
  };

  const handleAdvanceStage = async (opp: Opportunity, nextStage: OpportunityStage) => {
    // Optimistic update for instantaneous smooth Framer Motion layout animation
    setOpportunities((prev) => prev.map((o) => (o.id === opp.id ? { ...o, stage: nextStage } : o)));
    try {
      await transitionOpportunityStage(
        opp.id,
        nextStage,
        currentUser,
        `Stage transitioned to ${nextStage.replace("_", " ")}`,
        opp.stageHistory || [],
      );
    } catch (err) {
      console.error("Failed to advance stage:", err);
    }
  };

  const handleTransitionStage = async (
    opp: Opportunity,
    nextStage: OpportunityStage,
    customNote?: string,
  ) => {
    // Optimistic update for instantaneous smooth Framer Motion layout animation
    setOpportunities((prev) => prev.map((o) => (o.id === opp.id ? { ...o, stage: nextStage } : o)));
    try {
      await transitionOpportunityStage(
        opp.id,
        nextStage,
        currentUser,
        customNote || `Stage transitioned to ${nextStage.replace("_", " ")}`,
        opp.stageHistory || [],
      );
    } catch (err) {
      console.error("Failed to transition stage:", err);
    }
  };

  const handleConfirmArchive = async (reason: string, notes?: string) => {
    if (!archiveOpp) return;
    try {
      await archiveOpportunity(
        archiveOpp.id,
        reason,
        currentUser,
        archiveOpp.stageHistory || [],
        notes,
      );
    } catch (err) {
      console.error("Failed to archive opportunity:", err);
    }
  };

  const handleUnarchive = async (opp: Opportunity) => {
    try {
      await unarchiveOpportunity(opp.id, "reviewing", currentUser, opp.stageHistory || []);
    } catch (err) {
      console.error("Failed to restore opportunity from archive:", err);
    }
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      await deleteOpportunity(id);
    } catch (err) {
      console.error("Failed to delete opportunity:", err);
    }
  };

  const handleSaveQualifyChecklist = async (checklist: QualifyChecklist) => {
    if (!qualifyingOpp) return;
    await saveQualifyChecklist(
      qualifyingOpp.id,
      checklist,
      currentUser,
      qualifyingOpp.stageHistory || [],
    );
  };

  // Metrics summary
  const activeCount = opportunities.filter(
    (o) => !["won", "lost", "no_go", "archived"].includes(o.stage),
  ).length;
  const draftingCount = opportunities.filter((o) => ["go", "drafting"].includes(o.stage)).length;
  const inReviewCount = opportunities.filter((o) =>
    ["in_review", "approved"].includes(o.stage),
  ).length;
  const wonCount = opportunities.filter((o) => o.stage === "won").length;
  const archivedCount = opportunities.filter((o) => o.stage === "archived").length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Opportunity Pipeline
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Track, qualify, and manage active RFP responses from initial intake to final outcome.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              title="Cards List View"
              className={`rounded-md p-1.5 transition-all ${
                viewMode === "cards"
                  ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("kanban")}
              title="Kanban Board View"
              className={`rounded-md p-1.5 transition-all ${
                viewMode === "kanban"
                  ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>

          {/* Kanban Density Toggle */}
          {viewMode === "kanban" && (
            <div className="flex items-center rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => handleKanbanCardViewChange("standard")}
                title="Standard Kanban View"
                aria-pressed={kanbanCardView === "standard"}
                className={`btn-animate inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                  kanbanCardView === "standard"
                    ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                <Rows3 className="h-3.5 w-3.5" />
                <span>Standard</span>
              </button>
              <button
                type="button"
                onClick={() => handleKanbanCardViewChange("detailed")}
                title="Detailed Kanban View"
                aria-pressed={kanbanCardView === "detailed"}
                className={`btn-animate inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                  kanbanCardView === "detailed"
                    ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                    : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                }`}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>Detailed</span>
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsAnalyticsModalOpen(true)}
            className="btn-animate inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <PieChart className="h-4 w-4 text-[#2ba5b5]" />
            <span>Win/Loss Insights</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setEditingOpp(null);
              setIsOppModalOpen(true);
            }}
            className="btn-animate inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-[#238997]"
          >
            <Plus className="h-4 w-4" />
            <span>New Opportunity</span>
          </button>
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="glass rounded-xl p-4 border-l-4 border-l-[#2ba5b5]">
          <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
            Active Proposals
          </p>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{activeCount}</h3>
        </div>
        <div className="glass rounded-xl p-4 border-l-4 border-l-blue-500">
          <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
            Drafting
          </p>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{draftingCount}</h3>
        </div>
        <div className="glass rounded-xl p-4 border-l-4 border-l-purple-500">
          <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
            In Review
          </p>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{inReviewCount}</h3>
        </div>
        <div className="glass rounded-xl p-4 border-l-4 border-l-emerald-500">
          <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
            Contracts Won
          </p>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{wonCount}</h3>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="glass flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by client, contract title, owner, or service..."
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-hidden focus:border-[#2ba5b5] focus:ring-2 focus:ring-[#2ba5b5]/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-2xs outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
          >
            <option value="all">All Active Stages</option>
            <option value="reviewing">Intake / Reviewing</option>
            <option value="go">Qualified (Go)</option>
            <option value="no_go">Declined (No-Go)</option>
            <option value="drafting">Drafting</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="submitted">Submitted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="archived">Archived / Ended Early ({archivedCount})</option>
          </select>
        </div>
      </div>

      {/* Content Rendering (Cards or Kanban) */}
      {loading ? (
        <div className="glass flex items-center justify-center rounded-2xl p-12 text-slate-400">
          <RefreshCw className="h-6 w-6 animate-spin text-[#2ba5b5]" />
          <span className="ml-3 text-sm">Loading opportunity pipeline...</span>
        </div>
      ) : filteredOpportunities.length === 0 ? (
        <div className="glass flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 p-12 text-center dark:border-slate-800">
          <div className="rounded-full bg-teal-50 p-3 text-[#2ba5b5] dark:bg-teal-950/60">
            <FolderKanban className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
            {opportunities.length === 0
              ? "No RFP opportunities in pipeline"
              : "No opportunities match your search filter"}
          </h3>
          <p className="mt-1 max-w-sm text-xs text-slate-500 dark:text-slate-400">
            {opportunities.length === 0
              ? "Create a new RFP opportunity above to begin tracking intake, qualification criteria, and response drafting."
              : "Try adjusting your search terms or clearing the stage filter."}
          </p>
          {opportunities.length === 0 && (
            <button
              type="button"
              onClick={() => {
                setEditingOpp(null);
                setIsOppModalOpen(true);
              }}
              className="btn-animate mt-5 inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
            >
              <Plus className="h-4 w-4" />
              <span>Create First Opportunity</span>
            </button>
          )}
        </div>
      ) : viewMode === "kanban" ? (
        <PipelineKanban
          opportunities={filteredOpportunities}
          onOpenQualify={(opp: Opportunity) => {
            setQualifyingOpp(opp);
            setIsQualifyModalOpen(true);
          }}
          onOpenEdit={(opp: Opportunity) => {
            setEditingOpp(opp);
            setIsOppModalOpen(true);
          }}
          onAdvanceStage={handleAdvanceStage}
          onTransitionStage={handleTransitionStage}
          onOpenWorkspace={(opp: Opportunity) => {
            navigate(`/opportunities/${opp.id}`);
          }}
          cardViewMode={kanbanCardView}
          onCardViewModeChange={handleKanbanCardViewChange}
        />
      ) : (
        <div className="space-y-4">
          {filteredOpportunities.map((opp) => {
            // Stage badge colors
            const stageBadgeColors: Record<OpportunityStage, string> = {
              reviewing: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
              no_go: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
              go: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300",
              drafting: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
              in_review: "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300",
              approved: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
              submitted: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300",
              won: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
              lost: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
              archived: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
            };

            return (
              <ExpandableCard
                key={opp.id}
                title={opp.client}
                badge={opp.stage.toUpperCase().replace("_", " ")}
                badgeColor={stageBadgeColors[opp.stage] || "bg-slate-100 text-slate-700"}
                summary={
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {opp.title}
                    </span>
                    <span>•</span>
                    <span>
                      Owner:{" "}
                      <strong className="text-slate-700 dark:text-slate-300">{opp.owner}</strong>
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1 font-medium">
                      <Clock className="h-3 w-3" />
                      Due: {opp.dueDate}
                    </span>
                    {opp.decision && (
                      <>
                        <span>•</span>
                        <span
                          className={`font-bold ${
                            opp.decision === "go"
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-rose-600 dark:text-rose-400"
                          }`}
                        >
                          Decision: {opp.decision.toUpperCase()}
                        </span>
                      </>
                    )}
                    {opp.requirementsSummary && (
                      <>
                        <span>•</span>
                        <span className="inline-flex items-center gap-1 text-[#2ba5b5] font-semibold">
                          <Sparkles className="h-3 w-3" />
                          {opp.requirementsSummary.total} Reqs
                        </span>
                      </>
                    )}
                  </div>
                }
                details={
                  <div className="space-y-4">
                    {/* Services and Dates Grid */}
                    <div className="grid gap-3 sm:grid-cols-2 text-xs">
                      <div>
                        <h4 className="font-semibold text-slate-700 dark:text-slate-300">
                          Scope & Required Services:
                        </h4>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {opp.services.length > 0 ? (
                            opp.services.map((svc) => (
                              <span
                                key={svc}
                                className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                              >
                                {svc}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-400 italic">None specified</span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div>
                          <span className="text-slate-500">Questions Deadline: </span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {opp.questionsDueDate || "Not specified"}
                          </span>
                        </div>
                        {opp.noGoReason && (
                          <div>
                            <span className="text-rose-600 font-semibold">No-Go Reason: </span>
                            <span className="text-slate-700 dark:text-slate-300">
                              {opp.noGoReason}
                            </span>
                          </div>
                        )}
                        {opp.documentPaths && opp.documentPaths.length > 0 && (
                          <div>
                            <span className="text-slate-500">Attachments: </span>
                            <span className="text-[#2ba5b5] font-mono">
                              {opp.documentPaths.length} file(s) in Storage
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stage History Timeline */}
                    {opp.stageHistory && opp.stageHistory.length > 0 && (
                      <StageHistoryTimeline history={opp.stageHistory} />
                    )}

                    {/* Action Bar */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Open Workspace Button */}
                        <button
                          type="button"
                          onClick={() => navigate(`/opportunities/${opp.id}`)}
                          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97]"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Open Workspace</span>
                        </button>

                        {/* Qualify Button */}
                        <button
                          type="button"
                          onClick={() => {
                            setQualifyingOpp(opp);
                            setIsQualifyModalOpen(true);
                          }}
                          className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
                        >
                          <ClipboardList className="h-3.5 w-3.5" />
                          <span>
                            {opp.qualifyChecklist
                              ? "Review Qualify Checklist"
                              : "Qualify Checklist"}
                          </span>
                        </button>

                        {/* Edit Opportunity Button */}
                        <button
                          type="button"
                          onClick={() => {
                            setEditingOpp(opp);
                            setIsOppModalOpen(true);
                          }}
                          className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        >
                          <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                          <span>Edit</span>
                        </button>

                        {/* Archive / Restore Button */}
                        {opp.stage !== "archived" ? (
                          <button
                            type="button"
                            onClick={() => setArchiveOpp(opp)}
                            title="Archive / End opportunity early"
                            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50/70 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300"
                          >
                            <Archive className="h-3.5 w-3.5 text-amber-600" />
                            <span>Archive</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleUnarchive(opp)}
                            title="Restore opportunity from archive"
                            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-medium text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/30 dark:text-teal-300"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span>Restore</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => setDeleteOpp(opp)}
                          title="Delete opportunity permanently"
                          className="btn-animate rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Stage Advancement & Move-back Controls */}
                      <div
                        className="flex flex-wrap items-center gap-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Stage Selector Dropdown */}
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-slate-400 uppercase font-semibold">
                            Stage:
                          </span>
                          <select
                            value={opp.stage}
                            onChange={(e) =>
                              handleTransitionStage(opp, e.target.value as OpportunityStage)
                            }
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-2xs outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            <option value="reviewing">Intake & Reviewing</option>
                            <option value="go">Qualified (Go)</option>
                            <option value="no_go">Declined (No-Go)</option>
                            <option value="drafting">Drafting</option>
                            <option value="in_review">In Review</option>
                            <option value="approved">Approved</option>
                            <option value="submitted">Submitted</option>
                            <option value="won">Won</option>
                            <option value="lost">Lost</option>
                            <option value="archived">Archived / Ended Early</option>
                          </select>
                        </div>

                        {/* Quick Step Buttons */}
                        {opp.stage === "drafting" && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                handleTransitionStage(opp, "reviewing", "Moved back to Intake")
                              }
                              title="Move back to Intake & Reviewing"
                              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <ArrowLeft className="h-3 w-3" />
                              <span>Intake</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTransitionStage(opp, "in_review")}
                              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1 text-xs font-semibold text-white hover:bg-purple-700"
                            >
                              <span>Submit for Review</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}

                        {opp.stage === "in_review" && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                handleTransitionStage(opp, "drafting", "Moved back to Drafting")
                              }
                              title="Move back to Drafting"
                              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <ArrowLeft className="h-3 w-3" />
                              <span>Drafting</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTransitionStage(opp, "approved")}
                              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                            >
                              <span>Approve Final</span>
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}

                        {opp.stage === "approved" && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                handleTransitionStage(opp, "in_review", "Returned to Review")
                              }
                              title="Move back to Review"
                              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <ArrowLeft className="h-3 w-3" />
                              <span>Review</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTransitionStage(opp, "submitted")}
                              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-cyan-600 px-3 py-1 text-xs font-semibold text-white hover:bg-cyan-700"
                            >
                              <span>Mark Submitted</span>
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}

                        {opp.stage === "submitted" && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                handleTransitionStage(opp, "approved", "Reverted Submitted status")
                              }
                              title="Move back to Approved"
                              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <ArrowLeft className="h-3 w-3" />
                              <span>Approved</span>
                            </button>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setPostMortemOpp(opp);
                                  setPostMortemInitialOutcome("won");
                                }}
                                className="btn-animate rounded-lg bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                              >
                                Won
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setPostMortemOpp(opp);
                                  setPostMortemInitialOutcome("lost");
                                }}
                                className="btn-animate rounded-lg bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-700"
                              >
                                Lost
                              </button>
                            </div>
                          </>
                        )}

                        {opp.stage === "go" && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                handleTransitionStage(opp, "reviewing", "Reverted qualification")
                              }
                              title="Move back to Intake"
                              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <ArrowLeft className="h-3 w-3" />
                              <span>Intake</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTransitionStage(opp, "drafting")}
                              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1 text-xs font-semibold text-white hover:bg-teal-700"
                            >
                              <span>Start Drafting</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}

                        {opp.stage === "reviewing" && (
                          <button
                            type="button"
                            onClick={() => handleTransitionStage(opp, "drafting")}
                            className="btn-animate inline-flex items-center gap-1 rounded-lg bg-[#2ba5b5] px-3 py-1 text-xs font-semibold text-white hover:bg-[#238997]"
                          >
                            <span>Drafting</span>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        )}

                        {opp.stage === "archived" && (
                          <button
                            type="button"
                            onClick={() => handleUnarchive(opp)}
                            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            <RotateCcw className="h-3 w-3 text-[#2ba5b5]" />
                            <span>Restore to Pipeline</span>
                          </button>
                        )}

                        {["won", "lost", "no_go"].includes(opp.stage) && (
                          <button
                            type="button"
                            onClick={() =>
                              handleTransitionStage(opp, "drafting", "Reopened opportunity")
                            }
                            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            <RotateCcw className="h-3 w-3 text-slate-400" />
                            <span>Reopen</span>
                          </button>
                        )}

                        {opp.outcome && (
                          <button
                            type="button"
                            onClick={() => {
                              setPostMortemOpp(opp);
                              setPostMortemInitialOutcome(opp.outcome || "won");
                            }}
                            className="btn-animate rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            Debrief
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                }
              />
            );
          })}
        </div>
      )}

      {/* Opportunity Modal (Add / Edit) */}
      <OpportunityModal
        isOpen={isOppModalOpen}
        onClose={() => {
          setIsOppModalOpen(false);
          setEditingOpp(null);
        }}
        onSave={handleSaveOpportunity}
        initialData={editingOpp}
        staff={staff}
        currentUser={currentUser}
      />

      {/* Qualify Modal */}
      <QualifyModal
        isOpen={isQualifyModalOpen}
        onClose={() => {
          setIsQualifyModalOpen(false);
          setQualifyingOpp(null);
        }}
        opportunity={qualifyingOpp}
        staff={staff}
        onSave={handleSaveQualifyChecklist}
      />

      {/* Post-Mortem & Outcome Modal */}
      {postMortemOpp && (
        <PostMortemModal
          isOpen={Boolean(postMortemOpp)}
          onClose={() => setPostMortemOpp(null)}
          opportunity={postMortemOpp}
          user={user}
          initialOutcome={postMortemInitialOutcome}
          onOpenHarvest={() => {
            const target = postMortemOpp;
            setPostMortemOpp(null);
            setHarvestOpp(target);
          }}
        />
      )}

      {/* Harvest Knowledge Modal */}
      {harvestOpp && (
        <HarvestLibraryModal
          isOpen={Boolean(harvestOpp)}
          onClose={() => setHarvestOpp(null)}
          opportunity={harvestOpp}
          user={user}
        />
      )}

      {/* Archive / End Early Modal */}
      {archiveOpp && (
        <ArchiveOpportunityModal
          isOpen={Boolean(archiveOpp)}
          onClose={() => setArchiveOpp(null)}
          opportunity={archiveOpp}
          onConfirmArchive={handleConfirmArchive}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteOpp && (
        <DeleteOpportunityModal
          isOpen={Boolean(deleteOpp)}
          onClose={() => setDeleteOpp(null)}
          opportunity={deleteOpp}
          onConfirmDelete={handleConfirmDelete}
          onArchiveInstead={(opp) => setArchiveOpp(opp)}
        />
      )}

      {/* Win/Loss Analytics Modal */}
      {isAnalyticsModalOpen && (
        <WinLossAnalyticsModal
          isOpen={isAnalyticsModalOpen}
          opportunities={opportunities}
          onClose={() => setIsAnalyticsModalOpen(false)}
        />
      )}
    </div>
  );
};
